import PropTypes from 'prop-types'
import { ErrorBoundary } from 'react-error-boundary'
import { useLocation } from 'react-router-dom'
import { reportError } from '@/lib/faro'
import ErrorFallback from './ErrorFallback'

/**
 * Reusable error boundary for a region of the page. Bakes in the project
 * conventions so call sites stay a one-line wrapper:
 *
 *   <SectionErrorBoundary label="dashboard-content">
 *       <NavRoutes />
 *   </SectionErrorBoundary>
 *
 * Place it around a layout's content region (so nav/sidebar survive) or around
 * an individual risky widget (so one widget failing doesn't take out the page).
 *
 * `resetKeys` is tied to `location.pathname` so navigating to a different route
 * automatically clears a stuck error — without this, an errored boundary would
 * keep showing the fallback even after the user clicks a nav link. Pass
 * `resetOnNavigate={false}` for widgets that should not reset on route change.
 *
 * `title` / `message` are forwarded to the fallback so a page-level vs
 * section-level boundary can word its message appropriately.
 */
const SectionErrorBoundary = ({
    children,
    label,
    title,
    message,
    resetOnNavigate = true
}) => {
    const { pathname } = useLocation()

    return (
        <ErrorBoundary
            fallbackRender={(props) => (
                <ErrorFallback {...props} title={title} message={message} />
            )}
            onError={(error, info) =>
                reportError(error, { label, componentStack: info?.componentStack })
            }
            resetKeys={resetOnNavigate ? [pathname] : []}
        >
            {children}
        </ErrorBoundary>
    )
}

SectionErrorBoundary.propTypes = {
    children: PropTypes.node,
    label: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    resetOnNavigate: PropTypes.bool
}

export default SectionErrorBoundary
